<template>
  <div
    class="rangeGridSquareComponent"
    @mouseover="e => $emit('mouseover', e)"
    @mouseout="e => $emit('mouseout', e)"
  >
    <div
      class="rangeGridSquareContainer"
      :class="{ dark: isDark, hasContent: hasContent }"
      :style="{ background: splitData.background, borderColor: splitData.borderColor }"
      @mousedown="mousedown"
      @mouseup="mouseup"
    >
      <div
        class="contentContainer"
        v-if="hasContent"
        :style="{ background: splitData.content.background }"
      >
        <div class="content">
          <div v-if="splitData.content.anyCardRules.length" class="anyCardRules rulesContainer">
            <div
              class="ruleContainer"
              v-for="ruleData in splitData.content.anyCardRules"
              :key="ruleData.suit"
            >
              <div class="anyCardRule rule">
                <Suit :suit="ruleData.suit" />
              </div>
            </div>
          </div>
          <div v-if="splitData.content.highCardRules.length" class="highCardRules rulesContainer">
            <div
              class="ruleContainer"
              v-for="ruleData in splitData.content.highCardRules"
              :key="ruleData.suit"
            >
              <div class="highCardRule rule">
                <div class="highCard">{{ highCard }}</div>
                <Suit :suit="ruleData.suit" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="text">{{ text }}</div>
      <div class="hoverTarget" @mouseenter="mouseenter"></div>
    </div>
  </div>
</template>

<script>
import { darken, isDark } from "../helpers/color";
import { squareComboStringsToSuitRules } from "../helpers/cards";
import Suit from "./Suit";

export default {
  name: "RangeGridSquare",
  components: { Suit },
  props: {
    squareString: { type: String, required: true },
    comboStrings: { type: Array, required: true },
    filteredComboStrings: { type: Array, required: true },
    subrangeByComboString: { type: Object, required: true },
    colorScheme: { type: Array, required: true },
    preferSuits: { type: Array, default: () => [] },
  },

  computed: {
    text() {
      return this.squareString.substr(0, 2);
    },
    isInRange() {
      for (let comboString of this.filteredComboStrings) {
        if (this.subrangeByComboString[comboString] > -1) return true;
      }
      return false;
    },
    isDark() {
      return this.hasContent
        ? isDark(this.splitData.content.background)
        : isDark(this.splitData.background);
    },
    hasContent() {
      return Object.keys(this.filteredSplit).length >= 2;
    },
    highCard() {
      return this.squareString.substr(0, 1);
    },
    splitData() {
      if (Object.keys(this.split).length < 2) {
        const isColored = Object.keys(this.filteredSplit).length > 0;
        const colorIndex = Object.keys(this.filteredSplit)[0];
        // If the square isn't in the range anymore, make it white
        const color = this.colorsByIndex[colorIndex];
        const style = isColored
          ? {
              background: color,
              borderColor: darken(color),
            }
          : {};
        return style;
      }
      const getNumberOfRulesMatchingSuits = (rules, suits) => {
        return rules.filter(rule => suits.includes(rule.suit)).length;
      };
      const sortedSplit = Object.entries(this.split)
        .map(([colorIndex, comboStrings]) => {
          const rules = squareComboStringsToSuitRules(this.squareString, comboStrings);
          return { colorIndex, comboStrings, rules };
        })
        .sort((a, b) => {
          // If one of the ranges is the "not in range" range, don't use that as the main content
          if (a.colorIndex == -1 || b.colorIndex == -1) {
            return b.colorIndex - a.colorIndex;
          }
          // Use the subrange with less suit rules as the main content.
          // If a subrange has no rules, though, use the other one. Generally speaking, just because one
          // combo set can be reduced to rules, that doesn't mean its inverse can, too.
          if (a.rules.length !== b.rules.length) {
            if (a.rules.length === 0) return 1;
            if (b.rules.length === 0) return -1;
            return a.rules.length - b.rules.length;
          }
          // If both have the same number of rules, prefer the ones that correspond to board suits.
          return (
            getNumberOfRulesMatchingSuits(b.rules, this.preferSuits) -
            getNumberOfRulesMatchingSuits(a.rules, this.preferSuits)
          );
        });
      const rules = sortedSplit[0].rules;
      const anyCardRules = rules.filter(ruleData => ruleData.rule === "any");
      const highCardRules = rules.filter(ruleData => ruleData.rule === "high");
      const backgroundColor = this.colorsByIndex[sortedSplit[1].colorIndex];

      return {
        background: backgroundColor,
        borderColor: darken(backgroundColor),
        content: {
          background: this.colorsByIndex[sortedSplit[0].colorIndex],
          anyCardRules,
          highCardRules,
        },
      };
    },
    split() {
      return this.getSplit(this.comboStrings);
    },
    filteredSplit() {
      return this.getSplit(this.filteredComboStrings);
    },
    colorsByIndex() {
      return { ...this.colorScheme, "-1": "#ffffff" };
    },
  },

  methods: {
    mousedown(e) {
      if (e.buttons !== 1) return;
      if (!this.isInRange) return;
      this.$emit("startPaint", this.squareString);
    },
    mouseup(e) {
      if (e.buttons !== 1) return;
      this.$emit("endPaint", this.squareString);
    },
    mouseenter(e) {
      if (e.buttons !== 1) return;
      if (!this.isInRange) return;
      this.$emit("continuePaint", this.squareString);
    },
    getSplit(comboStrings) {
      const split = {};
      for (const comboString of comboStrings) {
        const subrangeId = this.subrangeByComboString[comboString];
        if (split[subrangeId] === undefined) {
          split[subrangeId] = [];
        }
        split[subrangeId].push(comboString);
      }
      return split;
    },
  },
};
</script>

<style lang="scss" scoped>
.rangeGridSquareComponent {
  width: 100%;
  height: 100%;
}

.rangeGridSquareContainer {
  width: 100%;
  height: 100%;
  position: relative;
  border: 1px solid #999;
  display: flex;
  align-items: center;
  justify-content: center;

  .contentContainer {
    width: 85%;
    height: 85%;
    position: relative;
  }

  .content {
    position: absolute;
    top: 0;
    left: 0;
    font-size: 8px;
    color: black;
    font-weight: bold;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  &.dark .content {
    color: white;
  }

  .rulesContainer {
    padding: 0 2px;
    display: flex;
    width: 100%;
    height: 14px;
    .ruleContainer {
      flex: 1;
      display: flex;
      position: relative;
      &:last-child {
        justify-content: flex-end;
        .rule {
          transform-origin: right;
          right: 0;
        }
      }
      &:first-child {
        justify-content: flex-start;
        .rule {
          transform-origin: left;
          left: 0;
        }
      }
    }
    .rule {
      position: absolute;
      display: flex;
      align-items: center;
      transform: scaleX(0.75);
    }
    .highCard {
      font-size: 10px;
      margin-right: 2px;
    }
  }

  .text {
    position: absolute;
    top: 0;
    left: -1px;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    opacity: 0.15;
    font-size: 25px;
    letter-spacing: -2px;
  }
  &.dark .text {
    color: white;
    opacity: 0.6;
  }
  &.hasContent .text {
    opacity: 0.2;
  }

  .hoverTarget {
    width: 110%;
    height: 110%;
    position: absolute;
    top: -5%;
    left: -5%;
    border-radius: 100%;
  }
}
</style>
